import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Group,
  Image,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconCheck,
  IconMicrophone,
  IconPlayerStopFilled,
  IconSend,
  IconVolume,
  IconWaveSine,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError, raw, unwrap } from '../api/client';
import { background, finishSession, getSession, reply, transcribe } from '../api/gen';
import type { Message } from '../api/gen';
import { TutorText } from '../components/TutorText';
import { useCredential, useOrg, useSession } from '../lib/session';
import { accent } from '../theme';
import { startLiveSession, liveSupported, type LiveHandle, type LiveStatus } from '../voice/liveSession';
import { cancelSpeech, speak, speechSupported } from '../voice/speech';
import { useRecorder, recorderSupported } from '../voice/useRecorder';

// The dialogue.
//
// Three ways to answer, and they all end up in the same transcript: typing,
// one recorded clip at a time, and the hands-free conversation. The last one is
// the only place where audio does not pass through this app — the browser holds
// an ephemeral token and talks to the provider itself — and it still writes
// each completed turn back here, so the parent's history and the eval sweep see
// a spoken session exactly as they see a typed one.
export function ChatPage() {
  const { id = '' } = useParams();
  const org = useOrg();
  const credential = useCredential();
  const { who, leave } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const viewport = useRef<HTMLDivElement>(null);

  const [draft, setDraft] = useState('');
  const [recording, setRecording] = useState(false);
  const [live, setLive] = useState<LiveHandle | null>(null);
  const [liveStatus, setLiveStatus] = useState<LiveStatus>('idle');
  const [speakingIndex, setSpeakingIndex] = useState<number | null>(null);
  const { record, stop: stopRecording } = useRecorder();

  const a = accent(who.kind === 'child' ? who.mode : 'junior');

  const { data, isLoading, error } = useQuery({
    queryKey: ['session', id],
    queryFn: () => unwrap(getSession({ path: { id }, headers: org })),
  });

  const messages = useMemo(() => data?.messages ?? [], [data]);
  const session = data?.session;

  // Scroll to the newest question whenever one arrives.
  useEffect(() => {
    viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  // Leaving the page stops the voice. Nothing is more startling than a tutor
  // that keeps talking from a screen you have left.
  useEffect(
    () => () => {
      cancelSpeech();
      live?.stop();
    },
    [live],
  );

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['session', id] });
  }, [qc, id]);

  const fail = (err: unknown) => {
    const message =
      err instanceof ApiError && err.status === 503
        ? 'Sokrates ist gerade nicht erreichbar. Versuch es gleich noch einmal.'
        : (err as Error).message;
    notifications.show({ color: 'red', message });
  };

  const answer = useMutation({
    mutationFn: (message: string) =>
      unwrap(reply({ path: { id }, headers: org, body: { message } })),
    onSuccess: () => {
      setDraft('');
      refresh();
    },
    onError: fail,
  });

  const explain = useMutation({
    mutationFn: () => unwrap(background({ path: { id }, headers: org })),
    onSuccess: (body) =>
      body &&
      notifications.show({
        title: 'Für dich zum Mitlesen',
        message: body.explanation,
        autoClose: false,
        color: a.color,
      }),
    onError: fail,
  });

  const finish = useMutation({
    mutationFn: () => unwrap(finishSession({ path: { id }, headers: org })),
    onSuccess: () => navigate('/'),
    onError: fail,
  });

  const sendClip = useMutation({
    mutationFn: async (clip: Blob) => {
      const audio = await toBase64(clip);
      const heard = await unwrap(
        transcribe({ path: { id }, headers: org, body: { audio, mimeType: 'audio/wav' } }),
      );
      const text = heard?.text?.trim();
      if (!text) throw new Error('Ich habe nichts verstanden. Sag es noch einmal.');
      return unwrap(reply({ path: { id }, headers: org, body: { message: text } }));
    },
    onSuccess: refresh,
    onError: fail,
  });

  const onRecord = async () => {
    if (recording) {
      stopRecording();
      return;
    }
    setRecording(true);
    try {
      const clip = await record();
      if (clip) sendClip.mutate(clip.blob);
    } catch {
      notifications.show({ color: 'red', message: 'Ich komme nicht ans Mikrofon.' });
    } finally {
      setRecording(false);
    }
  };

  const onSpeak = async (index: number) => {
    setSpeakingIndex(index);
    await speak(id, index, { orgId: org['X-Org-ID'], credential });
    setSpeakingIndex(null);
  };

  const toggleLive = async () => {
    if (live) {
      live.stop();
      setLive(null);
      return;
    }
    try {
      // The server voices the unanswered question first; it knows which one.
      const handle = await startLiveSession(
        id,
        {
          onStatus: setLiveStatus,
          onTurn: refresh,
          onError: (message) => notifications.show({ color: 'red', message }),
        },
        { orgId: org['X-Org-ID'] },
      );
      setLive(handle);
    } catch (err) {
      fail(err);
    }
  };

  if (isLoading) {
    return (
      <Group justify="center" mt="xl">
        <Loader />
      </Group>
    );
  }
  if (error || !session) {
    return (
      <Alert color="red" m="md">
        {(error as Error)?.message ?? 'Diese Sitzung gibt es nicht.'}
      </Alert>
    );
  }

  const busy = answer.isPending || sendClip.isPending;
  const finished = session.status === 'finished';

  return (
    <Stack h="100dvh" gap={0} style={{ background: a.soft }}>
      <Paper p="sm" withBorder radius={0}>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <ActionIcon variant="subtle" aria-label="Zurück" onClick={() => navigate('/')}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Box>
              <Title order={5} lineClamp={1}>
                {session.topic || 'Deine Frage'}
              </Title>
              {session.tasks.length > 0 ? (
                <Text size="xs" c="dimmed">
                  {session.tasks.length} Aufgabe{session.tasks.length === 1 ? '' : 'n'}
                </Text>
              ) : null}
            </Box>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <Button
              size="xs"
              variant="subtle"
              onClick={() => explain.mutate()}
              loading={explain.isPending}
            >
              Erklär mir das Blatt
            </Button>
            <Button
              size="xs"
              variant="light"
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={() => finish.mutate()}
              loading={finish.isPending}
              disabled={finished}
            >
              Fertig
            </Button>
          </Group>
        </Group>
      </Paper>

      <ScrollArea style={{ flex: 1, minHeight: 0 }} viewportRef={viewport} p="md">
        <Stack gap="md" maw={720} mx="auto">
          <Worksheet sessionId={id} orgId={org['X-Org-ID']} credential={credential} />
          {messages.map((m) => (
            <Bubble
              key={m.index}
              message={m}
              accentColor={a.color}
              speaking={speakingIndex === m.index}
              onSpeak={speechSupported ? () => void onSpeak(m.index) : undefined}
            />
          ))}
          {busy ? (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Sokrates denkt nach…
              </Text>
            </Group>
          ) : null}
        </Stack>
      </ScrollArea>

      <Paper p="sm" withBorder radius={0}>
        <Stack gap="xs" maw={720} mx="auto" w="100%">
          {live ? (
            <Alert color={a.color} variant="light" py="xs">
              {liveStatus === 'speaking'
                ? 'Sokrates spricht…'
                : liveStatus === 'connecting'
                  ? 'Verbindung…'
                  : 'Ich höre zu. Sag einfach los.'}
            </Alert>
          ) : null}
          <Group gap="xs" wrap="nowrap" align="flex-end">
            <Textarea
              flex={1}
              autosize
              minRows={1}
              maxRows={5}
              placeholder={finished ? 'Diese Sitzung ist beendet.' : 'Deine Antwort…'}
              value={draft}
              disabled={busy || finished || !!live}
              onChange={(e) => setDraft(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && draft.trim()) {
                  e.preventDefault();
                  answer.mutate(draft.trim());
                }
              }}
            />
            {recorderSupported() && !live ? (
              <Tooltip label={recording ? 'Fertig' : 'Antwort sprechen'}>
                <ActionIcon
                  size="lg"
                  variant={recording ? 'filled' : 'light'}
                  color={recording ? 'red' : a.color}
                  aria-label="Antwort sprechen"
                  disabled={busy || finished}
                  onClick={() => void onRecord()}
                >
                  {recording ? <IconPlayerStopFilled size={20} /> : <IconMicrophone size={20} />}
                </ActionIcon>
              </Tooltip>
            ) : null}
            {liveSupported() ? (
              <Tooltip label={live ? 'Gespräch beenden' : 'Frei sprechen'}>
                <ActionIcon
                  size="lg"
                  variant={live ? 'filled' : 'light'}
                  color={live ? 'red' : a.color}
                  aria-label="Freies Gespräch"
                  disabled={finished}
                  onClick={() => void toggleLive()}
                >
                  <IconWaveSine size={20} />
                </ActionIcon>
              </Tooltip>
            ) : null}
            <ActionIcon
              size="lg"
              variant="filled"
              color={a.color}
              aria-label="Absenden"
              disabled={!draft.trim() || busy || finished || !!live}
              onClick={() => answer.mutate(draft.trim())}
            >
              <IconSend size={20} />
            </ActionIcon>
          </Group>
          <Group justify="flex-end">
            <Button size="compact-xs" variant="subtle" color="gray" onClick={leave}>
              Abmelden
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}

function Bubble({
  message,
  accentColor,
  speaking,
  onSpeak,
}: {
  message: Message;
  accentColor: string;
  speaking: boolean;
  onSpeak?: () => void;
}) {
  const mine = message.role === 'user';
  return (
    <Group justify={mine ? 'flex-end' : 'flex-start'} align="flex-end" gap="xs" wrap="nowrap">
      {!mine && onSpeak ? (
        <ActionIcon
          variant="subtle"
          aria-label="Vorlesen"
          loading={speaking}
          onClick={onSpeak}
          mb={6}
        >
          <IconVolume size={18} />
        </ActionIcon>
      ) : null}
      <Card
        withBorder={!mine}
        shadow={mine ? 'none' : 'xs'}
        padding="sm"
        maw="80%"
        bg={mine ? `${accentColor}.6` : 'white'}
        c={mine ? 'white' : undefined}
      >
        {mine ? <Text>{message.content}</Text> : <TutorText>{message.content}</TutorText>}
      </Card>
    </Group>
  );
}

// The photograph, while the session is active. It is a picture of a child's
// homework, so the server marks it private and no-store and drops it when the
// session ends; a 404 here means exactly that and is not an error.
function Worksheet({
  sessionId,
  orgId,
  credential,
}: {
  sessionId: string;
  orgId: string;
  credential: ReturnType<typeof useCredential>;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    let objectURL = '';
    void (async () => {
      try {
        const response = await raw(`/sessions/${sessionId}/worksheet`, { orgId, credential });
        objectURL = URL.createObjectURL(await response.blob());
        if (!revoked) setUrl(objectURL);
      } catch {
        // no worksheet for this session: it was a typed question, or the
        // session has ended and the photograph is gone.
      }
    })();
    return () => {
      revoked = true;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [sessionId, orgId, credential]);

  if (!url) return null;
  return <Image src={url} alt="Dein Arbeitsblatt" radius="md" mah={220} fit="contain" />;
}

async function toBase64(blob: Blob): Promise<string> {
  const buffer = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < buffer.length; i++) binary += String.fromCharCode(buffer[i]);
  return btoa(binary);
}
